import { useState } from 'react';
import { useCart } from '../../contexts/CartContext';

interface DeliveryAddress {
  cep: string;
  rua: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  uf: string;
}

export default function CartPage() {
  const { items, updateQuantity, removeItem, getTotalPrice, clearCart } = useCart();
  const [step, setStep] = useState<'cart' | 'address' | 'payment'>('cart');
  const [address, setAddress] = useState<DeliveryAddress>({
    cep: '',
    rua: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    uf: '',
  });
  const [paymentMethod, setPaymentMethod] = useState<'pix' | 'cartao' | 'dinheiro'>('pix');
  const [loadingCep, setLoadingCep] = useState(false);

  const fetchAddress = async (cep: string) => {
    const cleanCep = cep.replace(/\D/g, '');
    if (cleanCep.length !== 8) return;

    setLoadingCep(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      const data = await response.json();
      
      if (!data.erro) {
        setAddress((prev) => ({
          ...prev,
          rua: data.logradouro || '',
          bairro: data.bairro || '',
          cidade: data.localidade || '',
          uf: data.uf || '',
        }));
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
    } finally {
      setLoadingCep(false);
    }
  };

  const handleCepChange = (value: string) => {
    const formatted = value.replace(/\D/g, '').slice(0, 8);
    setAddress((prev) => ({ ...prev, cep: formatted }));
    
    if (formatted.length === 8) {
      fetchAddress(formatted);
    }
  };

  const formatCep = (cep: string) => {
    return cep.replace(/(\d{5})(\d{3})/, '$1-$2');
  };

  const isAddressValid = () => {
    return (
      address.cep.length === 8 &&
      address.rua.trim() !== '' &&
      address.numero.trim() !== '' &&
      address.bairro.trim() !== '' &&
      address.cidade.trim() !== '' &&
      address.uf.trim() !== ''
    );
  };

  const generateWhatsAppMessage = () => {
    const itemsList = items
      .map((item) => `• ${item.name} – ${item.quantity}x – R$ ${(item.price * item.quantity).toFixed(2)}`)
      .join('\n');

    const paymentText = {
      pix: '💳 Pix',
      cartao: '💳 Cartão',
      dinheiro: '💵 Dinheiro',
    }[paymentMethod];

    const message = `🍔 NOVO PEDIDO 🍟

🧾 Pedido:
${itemsList}

📍 Endereço de Entrega:
${address.rua}, nº ${address.numero}${address.complemento ? ` - ${address.complemento}` : ''}
${address.bairro} – ${address.cidade}/${address.uf}
CEP: ${formatCep(address.cep)}

💰 Forma de Pagamento:
${paymentText}

💵 Total: R$ ${getTotalPrice().toFixed(2)}

📲 Pedido enviado pelo cardápio digital`;

    return encodeURIComponent(message);
  };

  const handleFinishOrder = () => {
    const message = generateWhatsAppMessage();
    const whatsappUrl = `https://wa.me/5511965753001?text=${message}`;
    window.open(whatsappUrl, '_blank');
    clearCart();
    window.REACT_APP_NAVIGATE('/');
  };

  if (items.length === 0 && step === 'cart') {
    return (
      <div className="min-h-screen bg-[#F5F5F5] pt-24 pb-12">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center py-20">
            <i className="ri-shopping-cart-line text-8xl text-gray-300 mb-6"></i>
            <h2 className="text-3xl font-bold text-[#2D303D] mb-4">Carrinho Vazio</h2>
            <p className="text-gray-600 mb-8">Adicione produtos ao seu carrinho para continuar</p>
            <button
              onClick={() => window.REACT_APP_NAVIGATE('/')}
              className="bg-[#FF6B35] hover:bg-[#FF6B35]/90 text-white font-bold px-8 py-4 rounded-full transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              Ver Cardápio
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5] pt-24 pb-12">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-8 gap-4">
            <div className={`flex items-center gap-2 ${step === 'cart' ? 'text-[#FF6B35]' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step === 'cart' ? 'bg-[#FF6B35] text-white' : 'bg-gray-300'}`}>
                1
              </div>
              <span className="font-semibold hidden sm:inline">Carrinho</span>
            </div>
            <div className="w-12 h-1 bg-gray-300"></div>
            <div className={`flex items-center gap-2 ${step === 'address' ? 'text-[#FF6B35]' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step === 'address' ? 'bg-[#FF6B35] text-white' : 'bg-gray-300'}`}>
                2
              </div>
              <span className="font-semibold hidden sm:inline">Endereço</span>
            </div>
            <div className="w-12 h-1 bg-gray-300"></div>
            <div className={`flex items-center gap-2 ${step === 'payment' ? 'text-[#FF6B35]' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step === 'payment' ? 'bg-[#FF6B35] text-white' : 'bg-gray-300'}`}>
                3
              </div>
              <span className="font-semibold hidden sm:inline">Pagamento</span>
            </div>
          </div>

          {/* Cart Items */}
          {step === 'cart' && (
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-[#2D303D] mb-6">Seu Carrinho</h2>
              <div className="space-y-4">
                {items.map((item) => (
                  <div key={item.id} className="flex gap-4 p-4 bg-gray-50 rounded-xl">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-24 h-24 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <h3 className="font-bold text-[#2D303D] mb-2">{item.name}</h3>
                      <p className="text-[#FF6B35] font-bold text-lg">R$ {item.price.toFixed(2)}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="w-8 h-8 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded-full transition-colors cursor-pointer"
                        >
                          <i className="ri-subtract-line"></i>
                        </button>
                        <span className="font-semibold w-8 text-center">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="w-8 h-8 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded-full transition-colors cursor-pointer"
                        >
                          <i className="ri-add-line"></i>
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-col items-end justify-between">
                      <button
                        onClick={() => removeItem(item.id)}
                        className="text-red-500 hover:text-red-700 cursor-pointer"
                      >
                        <i className="ri-delete-bin-line text-xl"></i>
                      </button>
                      <p className="font-bold text-[#2D303D]">R$ {(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t mt-6 pt-6">
                <div className="flex justify-between items-center mb-6">
                  <span className="text-xl font-bold text-[#2D303D]">Total:</span>
                  <span className="text-3xl font-bold text-[#FF6B35]">R$ {getTotalPrice().toFixed(2)}</span>
                </div>
                <button
                  onClick={() => setStep('address')}
                  className="w-full bg-[#FF6B35] hover:bg-[#FF6B35]/90 text-white font-bold py-4 rounded-full transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
                >
                  Continuar para Endereço
                </button>
              </div>
            </div>
          )}

          {/* Address Form */}
          {step === 'address' && (
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-[#2D303D] mb-6">Endereço de Entrega</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-[#2D303D] mb-2">CEP *</label>
                  <input
                    type="text"
                    value={formatCep(address.cep)}
                    onChange={(e) => handleCepChange(e.target.value)}
                    placeholder="00000-000"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B35] text-sm"
                    maxLength={9}
                  />
                  {loadingCep && <p className="text-sm text-gray-500 mt-1">Buscando endereço...</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#2D303D] mb-2">Rua *</label>
                  <input
                    type="text"
                    value={address.rua}
                    onChange={(e) => setAddress({ ...address, rua: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B35] text-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#2D303D] mb-2">Número *</label>
                    <input
                      type="text"
                      value={address.numero}
                      onChange={(e) => setAddress({ ...address, numero: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B35] text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#2D303D] mb-2">Complemento</label>
                    <input
                      type="text"
                      value={address.complemento}
                      onChange={(e) => setAddress({ ...address, complemento: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B35] text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#2D303D] mb-2">Bairro *</label>
                  <input
                    type="text"
                    value={address.bairro}
                    onChange={(e) => setAddress({ ...address, bairro: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B35] text-sm"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-semibold text-[#2D303D] mb-2">Cidade *</label>
                    <input
                      type="text"
                      value={address.cidade}
                      onChange={(e) => setAddress({ ...address, cidade: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B35] text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#2D303D] mb-2">UF *</label>
                    <input
                      type="text"
                      value={address.uf}
                      onChange={(e) => setAddress({ ...address, uf: e.target.value.toUpperCase() })}
                      maxLength={2}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B35] text-sm uppercase"
                    />
                  </div>
                </div>
              </div>
              <div className="flex gap-4 mt-6">
                <button
                  onClick={() => setStep('cart')}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-[#2D303D] font-bold py-4 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  Voltar
                </button>
                <button
                  onClick={() => setStep('payment')}
                  disabled={!isAddressValid()}
                  className="flex-1 bg-[#FF6B35] hover:bg-[#FF6B35]/90 text-white font-bold py-4 rounded-full transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Continuar
                </button>
              </div>
            </div>
          )}

          {/* Payment Method */}
          {step === 'payment' && (
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-[#2D303D] mb-6">Forma de Pagamento</h2>
              <div className="space-y-4 mb-6">
                <button
                  onClick={() => setPaymentMethod('pix')}
                  className={`w-full p-4 rounded-xl border-2 transition-all cursor-pointer flex items-center gap-4 ${
                    paymentMethod === 'pix' ? 'border-[#FF6B35] bg-[#FF6B35]/5' : 'border-gray-300 hover:border-[#FF6B35]/50'
                  }`}
                >
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    paymentMethod === 'pix' ? 'border-[#FF6B35]' : 'border-gray-300'
                  }`}>
                    {paymentMethod === 'pix' && <div className="w-3 h-3 rounded-full bg-[#FF6B35]"></div>}
                  </div>
                  <i className="ri-qr-code-line text-2xl text-[#FF6B35]"></i>
                  <span className="font-semibold text-[#2D303D]">Pix</span>
                </button>
                <button
                  onClick={() => setPaymentMethod('cartao')}
                  className={`w-full p-4 rounded-xl border-2 transition-all cursor-pointer flex items-center gap-4 ${
                    paymentMethod === 'cartao' ? 'border-[#FF6B35] bg-[#FF6B35]/5' : 'border-gray-300 hover:border-[#FF6B35]/50'
                  }`}
                >
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    paymentMethod === 'cartao' ? 'border-[#FF6B35]' : 'border-gray-300'
                  }`}>
                    {paymentMethod === 'cartao' && <div className="w-3 h-3 rounded-full bg-[#FF6B35]"></div>}
                  </div>
                  <i className="ri-bank-card-line text-2xl text-[#FF6B35]"></i>
                  <span className="font-semibold text-[#2D303D]">Cartão</span>
                </button>
                <button
                  onClick={() => setPaymentMethod('dinheiro')}
                  className={`w-full p-4 rounded-xl border-2 transition-all cursor-pointer flex items-center gap-4 ${
                    paymentMethod === 'dinheiro' ? 'border-[#FF6B35] bg-[#FF6B35]/5' : 'border-gray-300 hover:border-[#FF6B35]/50'
                  }`}
                >
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    paymentMethod === 'dinheiro' ? 'border-[#FF6B35]' : 'border-gray-300'
                  }`}>
                    {paymentMethod === 'dinheiro' && <div className="w-3 h-3 rounded-full bg-[#FF6B35]"></div>}
                  </div>
                  <i className="ri-money-dollar-circle-line text-2xl text-[#FF6B35]"></i>
                  <span className="font-semibold text-[#2D303D]">Dinheiro</span>
                </button>
              </div>

              {/* Order Summary */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <h3 className="font-bold text-[#2D303D] mb-3">Resumo do Pedido</h3>
                <div className="space-y-2 text-sm">
                  {items.map((item) => (
                    <div key={item.id} className="flex justify-between">
                      <span>{item.name} x{item.quantity}</span>
                      <span className="font-semibold">R$ {(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t mt-3 pt-3 flex justify-between items-center">
                  <span className="font-bold text-[#2D303D]">Total:</span>
                  <span className="text-2xl font-bold text-[#FF6B35]">R$ {getTotalPrice().toFixed(2)}</span>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => setStep('address')}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-[#2D303D] font-bold py-4 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  Voltar
                </button>
                <button
                  onClick={handleFinishOrder}
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white font-bold py-4 rounded-full transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className="ri-whatsapp-fill text-xl"></i>
                  Finalizar no WhatsApp
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
